import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Form, Badge, Spinner, Alert, InputGroup, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { helpdeskAPI } from '../services/api';
import CreateTicketModal from './CreateTicketModal';

const TicketList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
  });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ordering: '-created_at',
      };
      
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.priority) {
        params.priority = filters.priority;
      }
      
      const response = await helpdeskAPI.getTickets(params);
      const ticketsData = response.data.results || response.data;
      console.log('Tickets data:', ticketsData); // Debug: see what we get
      if (ticketsData[0]) {
        console.log('First ticket comments:', ticketsData[0].comments); // Debug: check comments
      }
      setTickets(ticketsData);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTickets();

    // Auto-refresh every 10 seconds (increased from 5 to reduce conflicts)
    const interval = setInterval(() => {
      fetchTickets();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchTickets]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const handleTicketCreated = () => {
    fetchTickets();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-warning text-dark';
      case 'in_progress':
        return 'bg-info';
      case 'resolved':
        return 'bg-success';
      case 'closed':
        return 'bg-secondary';
      case 'pending_customer':
        return 'bg-warning';
      case 'pending_internal':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <div className="main-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-list me-2"></i>
          All Tickets
          <small style={{
            marginLeft: '12px',
            fontSize: '12px',
            color: '#4CAF50',
            fontWeight: 'normal',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#4CAF50',
              animation: 'pulse 2s infinite',
              display: 'inline-block'
            }}></span>
            Auto-updating
          </small>
        </h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <i className="fas fa-plus me-2"></i>
          New Ticket
        </button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup className="mb-3 mb-md-0">
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search tickets..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="pending_customer">Pending Customer</option>
                <option value="pending_internal">Pending Internal</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  onClick={() => setFilters({ search: '', status: '', priority: '' })}
                  className="flex-grow-1"
                >
                  <i className="fas fa-redo me-1"></i> Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading tickets...</span>
            </div>
          ) : tickets.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Ticket / Title</th>
                    <th style={{ width: '350px' }}>Latest Message</th>
                    <th style={{ width: '120px' }}>Status</th>
                    <th style={{ width: '120px' }}>Priority</th>
                    <th style={{ width: '180px' }}>Created By</th>
                    <th style={{ width: '150px' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => {
                    console.log('Ticket:', ticket.ticket_number, 'Description:', ticket.description, 'Comments:', ticket.comments);
                    // Handle both array and single comment formats
                    let latestComment = null;
                    if (ticket.comments) {
                      if (Array.isArray(ticket.comments) && ticket.comments.length > 0) {
                        latestComment = ticket.comments[ticket.comments.length - 1];
                      }
                    }
                    // Fallback: use description as first message if no comments
                    const displayMessage = latestComment 
                      ? `${latestComment.user_name}: ${latestComment.content}`
                      : (ticket.description ? ticket.description.substring(0, 100) : 'No messages yet');
                    
                    return (
                      <tr 
                        key={ticket.id}
                        onClick={() => handleTicketClick(ticket.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div>
                            <small className="text-primary fw-bold d-block">
                              #{ticket.ticket_number}
                            </small>
                            <strong>{ticket.title}</strong>
                          </div>
                        </td>
                        <td>
                          <div style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            color: latestComment ? '#333' : '#999',
                            fontSize: '14px',
                            fontStyle: latestComment ? 'normal' : 'italic'
                          }}>
                            {displayMessage}
                          </div>
                        </td>
                        <td>
                          <Badge className={getStatusBadgeClass(ticket.status)}>
                            {ticket.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          <Badge 
                            style={{ backgroundColor: ticket.priority_color || '#6c757d' }}
                          >
                            {ticket.priority_name || 'Normal'}
                          </Badge>
                        </td>
                        <td>
                          <small>{ticket.created_by_name || 'Unknown'}</small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(ticket.created_at).toLocaleString()}
                          </small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-muted py-5">
              <i className="fas fa-inbox display-1 mb-3"></i>
              <h3>No Tickets Found</h3>
              <p>
                {filters.search || filters.status 
                  ? 'No tickets match your search criteria. Try adjusting your filters.'
                  : 'Create your first ticket to get started.'}
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Create Your First Ticket
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <CreateTicketModal 
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onTicketCreated={handleTicketCreated}
      />
    </div>
  );
};

export default TicketList;
